import {
  ANNOTATIONS_BASE_QUERY_KEY,
  deleteDuplicateAnnotationAssignmentClasses,
  editAnnotationAssignment,
} from 'api/annotationAssignments';
import { AnnotationAssignmentUpdate, AnnotationClassRemovalOperation } from 'interfaces/annotation';
import { find } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { SubmitHandler } from 'react-hook-form';
import queryClient from 'utils/queryClient';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { useCasesParams } from 'utils/useCasesParams';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import AnnotationAssignmentDialog, { AssignmentFormValues } from './AnnotationAssignmentDialog';

interface EditAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  annotationAssignmentId: number;
  fetchOnlySlidesOfStudy: boolean;
  withLegacy: boolean;
}

export const EditAnnotationAssignmentDialog: React.FC<EditAssignmentDialogProps> = ({
  open,
  onClose,
  annotationAssignmentId,
  fetchOnlySlidesOfStudy,
  withLegacy,
}) => {
  const { casesParams } = useCasesParams();
  const { data: annotationAssignmentsResponse } = useAnnotationAssignments(
    casesParams?.studyId || casesParams?.filters?.studyId,
    withLegacy,
    fetchOnlySlidesOfStudy
  );

  const currentAssignment = find(
    annotationAssignmentsResponse?.annotationAssignments,
    (assignment) => assignment.annotationAssignmentId === annotationAssignmentId
  );

  const editAnnotationAssignmentMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Edit annotation assignment',
    mutationFn: editAnnotationAssignment,
    onSuccess: () => {
      enqueueSnackbar('Annotation assignment edited successfully', { variant: 'success' });
      queryClient.invalidateQueries([ANNOTATIONS_BASE_QUERY_KEY]);
      queryClient.invalidateQueries(['annotations']);
      onClose();
    },
  });

  const deleteDuplicateClassesMutation = useMutationWithErrorSnackbar({
    mutationDescription: 'Delete duplicate annotation assignment classes',
    mutationFn: deleteDuplicateAnnotationAssignmentClasses,
    onSuccess: ({ removedDuplicateCount }) => {
      enqueueSnackbar(
        `${removedDuplicateCount} duplicate class ${removedDuplicateCount === 1 ? 'record' : 'records'} deleted`,
        { variant: 'success' }
      );
      queryClient.invalidateQueries([ANNOTATIONS_BASE_QUERY_KEY]);
      queryClient.invalidateQueries(['annotations']);
    },
  });

  const [classesToRemoveWithActions, setClassesToRemoveWithActions] = React.useState<AnnotationClassRemovalOperation[]>(
    []
  );

  const handleApply: SubmitHandler<AssignmentFormValues> = async (data) => {
    const annotationAssignmentUpdates: AnnotationAssignmentUpdate = data;

    editAnnotationAssignmentMutation.mutate({
      annotationAssignmentId,
      data: { ...annotationAssignmentUpdates, ...casesParams, classRemovalOperations: classesToRemoveWithActions },
    });
  };

  return (
    <>
      <AnnotationAssignmentDialog
        open={open}
        onClose={onClose}
        title={`Edit Annotation Assignment ${annotationAssignmentId}`}
        buttonText="Save"
        isLoadingButtonText="Saving..."
        handleApply={handleApply}
        isLoading={editAnnotationAssignmentMutation.isLoading}
        currentAssignment={currentAssignment}
        classesToRemoveWithActions={classesToRemoveWithActions}
        setClassesToRemoveWithActions={setClassesToRemoveWithActions}
        onDeleteDuplicateClasses={async (classNames) => {
          await deleteDuplicateClassesMutation.mutateAsync({ annotationAssignmentId, classNames });
        }}
        isDeletingDuplicateClasses={deleteDuplicateClassesMutation.isLoading}
      />
    </>
  );
};
